import { CallStateActionCodec, CallStateActionType } from '@engoo/realtime-protocol/callState';
import { PersistedActionCodec } from '@engoo/realtime-protocol/persisted';
import {
  getLockStatusName,
  LockState,
  LockStateLocked,
  LockStateOther,
  LockStatus,
} from 'app/models/rtc/callState/LockState';
import Logger from 'app/services/logger';
import { fold } from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/pipeable';
import { Record } from 'immutable';

/**
 * Specification details:
 *
 * 1. Multiple lock events of the same type are forbidden.
 * 2. Lock events occur in a particular order. An impending lock cannot come after a lock, for example.
 */
const updateLockState = (nextLockState: LockState, callState: CallState): CallState => {
  const { lockStatus: nextLockStatus } = nextLockState;
  const { lockStatus: previousLockStatus } = callState.lockState;

  if (previousLockStatus >= nextLockStatus) {
    Logger.error('Unexpected lock status transition.', {
      callState,
      nextLockStatus: getLockStatusName(nextLockStatus),
      previousLockStatus: getLockStatusName(previousLockStatus),
    });
    return callState;
  }

  return callState.set('lockState', nextLockState);
};

interface ICallState {
  lockState: LockState;
}

export class CallState extends Record<ICallState>({
  lockState: new LockStateOther({ lockStatus: LockStatus.Init }),
}) {}

export const callStateReducer = (state = new CallState(), action?: unknown): CallState => {
  return pipe(
    CallStateActionCodec.decode(action),
    fold(
      () => {
        return state;
      },
      callStateAction => {
        return pipe(
          PersistedActionCodec.decode(action),
          fold(
            () => {
              return state;
            },
            persistedAction => {
              const timestamp = new Date(persistedAction.timestamp);

              switch (callStateAction.type) {
                case CallStateActionType.Lock: {
                  const { lockTime: previous } = state.lockState;

                  // Handle both double lock, and locks coming in way after the lesson was supposed to end.
                  // In the latter case, the impending lock should set `previous`
                  const lockTime = previous && previous < timestamp ? previous : timestamp;

                  return updateLockState(
                    new LockStateLocked({
                      lockInitiator: callStateAction.initiator,
                      lockReason: callStateAction.lockReason,
                      lockStatus: LockStatus.Locked,
                      lockTime,
                    }),
                    state,
                  );
                }

                case CallStateActionType.Unlock: {
                  return updateLockState(
                    new LockStateOther({
                      // Don't change the lock time.
                      lockTime: state.lockState.lockTime,
                      lockStatus: LockStatus.Unlocked,
                    }),
                    state,
                  );
                }

                case CallStateActionType.ImpendingLock: {
                  const { lockTime: previous } = state.lockState;
                  const candidate = new Date(callStateAction.lockTime);

                  // Ignore subsequent changes to the lock time.
                  const lockTime = previous && previous < candidate ? previous : candidate;

                  return state.update('lockState', (lockState) => {
                    if (lockState instanceof LockStateOther) {
                      return lockState.set('lockTime', lockTime);
                    } else {
                      return lockState;
                    }
                  });
                }

                case CallStateActionType.DisableVideo: {
                  // The ability to fall back to Skype has been removed, so this
                  // shouldn't happen in practice, but we still need the `case`
                  // to satisfy TypeScript.
                  return state;
                }
              }
            },
          ),
        );
      },
    ),
  );
};

export default callStateReducer;
